/**
 * Audio playback state machine for the vinyl turntable player.
 * Manages the full lifecycle: idle → selecting → loading → cueing → playing ↔ paused → unloading.
 * Controls HTML5 Audio, tonearm angle (mapped to album progress), vinyl travel animation,
 * platter spin-up/deceleration, volume, and drag-to-seek via tonearm angle.
 */
import { useState, useRef, useCallback, useEffect, useMemo } from 'react';

export interface VinylTrack {
  title: string;
  src: string;
  duration: number; // in seconds
  artist?: string;
  isOriginal: boolean;
}

export interface VinylAlbum {
  id: string;
  title: string;
  cover: string;
  tracks: VinylTrack[];
}

export type PlayerState = 
  | 'idle'           // No album selected
  | 'selecting'      // Sleeve sliding out
  | 'loading'        // Vinyl traveling to platter
  | 'cueing'         // Tonearm moving to position
  | 'playing'        // Active playback
  | 'paused'         // Paused, tonearm lifted
  | 'seeking'        // Changing tracks
  | 'unloading';     // Vinyl returning to sleeve

// Animation timing constants (in ms)
const SLEEVE_SLIDE_DURATION = 300;
const VINYL_TRAVEL_DURATION = 1000;
const VINYL_SETTLE_DURATION = 300;
const TONEARM_LIFT_DURATION = 300;
const TONEARM_SWING_DURATION = 600;
const TONEARM_DROP_DURATION = 400;
const PLATTER_SPINUP_DURATION = 1500;
const PLATTER_DECEL_DURATION = 2000;

// Tonearm angle constants - exported for use in components
export const TONEARM_START_ANGLE = 8;   // Outer edge (start of record)
export const TONEARM_END_ANGLE = -13;   // Inner edge (near label)
export const TONEARM_REST_ANGLE = -30;  // Resting position (away from record)
export const TONEARM_RANGE = TONEARM_START_ANGLE - TONEARM_END_ANGLE; // Total sweep

// Calculate cumulative duration up to (but not including) a track
const getCumulativeDuration = (tracks: VinylTrack[], upToIndex: number): number => {
  return tracks.slice(0, upToIndex).reduce((sum, track) => sum + track.duration, 0);
};

// Get total album duration
const getTotalDuration = (tracks: VinylTrack[]): number => {
  return tracks.reduce((sum, track) => sum + track.duration, 0);
};

// Calculate tonearm angle based on overall album progress
// This creates realistic movement: starts at outer edge, ends near label
const getTonearmAngle = (
  trackIndex: number,
  trackProgress: number, // 0-1 within current track
  tracks: VinylTrack[]
): number => {
  const totalDuration = getTotalDuration(tracks);
  if (totalDuration === 0) return TONEARM_START_ANGLE;
  
  // Calculate elapsed time: all previous tracks + progress in current track
  const previousTracksDuration = getCumulativeDuration(tracks, trackIndex);
  const currentTrackElapsed = tracks[trackIndex]?.duration * trackProgress || 0;
  const totalElapsed = previousTracksDuration + currentTrackElapsed;
  
  // Overall progress through the album (0-1)
  const overallProgress = totalElapsed / totalDuration;
  
  // Interpolate tonearm angle based on overall progress
  return TONEARM_START_ANGLE - (overallProgress * TONEARM_RANGE);
};

export function useVinylPlayer() {
  const [state, setState] = useState<PlayerState>('idle');
  const [currentAlbum, setCurrentAlbum] = useState<VinylAlbum | null>(null);
  const [previousAlbum, setPreviousAlbum] = useState<VinylAlbum | null>(null);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const [vinylPosition, setVinylPosition] = useState<'sleeve' | 'traveling' | 'platter'>('sleeve');
  const [tonearmAngle, setTonearmAngle] = useState(TONEARM_REST_ANGLE); // Start at rest position
  const [tonearmLifted, setTonearmLifted] = useState(true);
  const [hoveredAlbumId, setHoveredAlbumId] = useState<string | null>(null);
  const [volume, setVolume] = useState(0.7); // 0-1
  // Mirrors `volume` so audio elements created inside stable callbacks start at the
  // level the fader shows, not the HTMLAudioElement default of 1.0.
  const volumeRef = useRef(volume);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressIntervalRef = useRef<number | null>(null);
  // Remembers tonearm position when pausing so resume can return to it
  const savedAngleRef = useRef<number>(TONEARM_START_ANGLE);
  // Prevents overlapping async operations (play/pause race conditions)
  const busyRef = useRef<boolean>(false);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  // Progress tracking - updates tonearm position based on playback progress
  useEffect(() => {
    if (state === 'playing' && audioRef.current && currentAlbum) {
      progressIntervalRef.current = window.setInterval(() => {
        if (audioRef.current && audioRef.current.duration) {
          const trackProgress = audioRef.current.currentTime / audioRef.current.duration;
          setProgress(trackProgress);
          
          // Calculate realistic tonearm angle based on overall album progress
          const angle = getTonearmAngle(
            currentTrackIndex,
            trackProgress,
            currentAlbum.tracks
          );
          setTonearmAngle(angle);
        }
      }, 100);
    } else {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    }
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [state, currentTrackIndex, currentAlbum]);

  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  // Unload current vinyl - returns it to the sleeve (fast version for paused state)
  const unloadVinyl = useCallback(async (fast = false) => {
    if (state === 'idle' || !currentAlbum) return;

    setState('unloading');

    // Stop audio immediately
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }

    if (!fast) {
      // Lift tonearm first
      setTonearmLifted(true);
      await delay(TONEARM_LIFT_DURATION);

      // Return tonearm to rest position
      setTonearmAngle(TONEARM_REST_ANGLE);
      await delay(TONEARM_SWING_DURATION);

      // Stop spinning with deceleration
      setIsSpinning(false);
      await delay(PLATTER_DECEL_DURATION / 2);
    } else {
      // Fast path: tonearm already at rest, platter already stopped
      setTonearmLifted(true);
      setTonearmAngle(TONEARM_REST_ANGLE);
      setIsSpinning(false);
    }

    // Vinyl travels back to sleeve
    setVinylPosition('traveling');
    await delay(VINYL_TRAVEL_DURATION);
    setVinylPosition('sleeve');
    await delay(SLEEVE_SLIDE_DURATION);

    // Store previous album for reference
    setPreviousAlbum(currentAlbum);
    setCurrentAlbum(null);
    setCurrentTrackIndex(0);
    setProgress(0);
    setState('idle');
  }, [state, currentAlbum]);

  const selectAlbum = useCallback(async (album: VinylAlbum) => {
    // If clicking same album that's already loaded, just toggle play/pause
    if (currentAlbum?.id === album.id && state !== 'idle') {
      return;
    }

    // If different album is currently loaded, unload it first
    if (currentAlbum && currentAlbum.id !== album.id && state !== 'idle') {
      await unloadVinyl();
      // Small delay between albums
      await delay(200);
    }

    // Now load the new album
    setState('selecting');
    setCurrentAlbum(album);
    setCurrentTrackIndex(0);
    setProgress(0);

    // Step 1: Sleeve slides out indication
    await delay(SLEEVE_SLIDE_DURATION);

    // Step 2: Vinyl travels from sleeve to turntable
    setState('loading');
    setVinylPosition('traveling');
    await delay(VINYL_TRAVEL_DURATION);

    // Step 3: Vinyl settles on platter
    setVinylPosition('platter');
    await delay(VINYL_SETTLE_DURATION);

    // Step 4: Start spinning AND cueing at the same time so button activates with spin
    setIsSpinning(true);

    // Step 5: Auto-start playback (runs in parallel with spin-up)
    await startPlayback(album, 0);
  }, [currentAlbum, state, unloadVinyl]);

  // Internal playback start - assumes vinyl is already on platter and spinning
  const startPlayback = useCallback(async (album: VinylAlbum, trackIndex: number) => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      setState('cueing');

      // Create audio element
      audioRef.current = new Audio(album.tracks[trackIndex].src);
      audioRef.current.volume = volumeRef.current;
      audioRef.current.onended = () => {
        if (trackIndex < album.tracks.length - 1) {
          seekTrack(trackIndex + 1);
        } else {
          pause();
        }
      };

      // Tonearm sequence: ensure lifted, then swing from rest to track position, then lower
      setTonearmLifted(true);
      setTonearmAngle(TONEARM_REST_ANGLE);
      await delay(TONEARM_LIFT_DURATION);

      const targetAngle = getTonearmAngle(trackIndex, 0, album.tracks);
      setTonearmAngle(targetAngle);
      await delay(TONEARM_SWING_DURATION);

      setTonearmLifted(false);
      await delay(TONEARM_DROP_DURATION);

      try {
        await audioRef.current.play();
        setState('playing');
      } catch (error) {
        console.error('Playback failed:', error);
        setState('paused');
      }
    } finally {
      busyRef.current = false;
    }
  }, []);

  const play = useCallback(async () => {
    if (!currentAlbum || state === 'playing' || busyRef.current) return;
    busyRef.current = true;
    try {
      if (state === 'paused') {
        setState('cueing');
        
        setIsSpinning(true);
        await delay(PLATTER_SPINUP_DURATION / 2);

        setTonearmAngle(savedAngleRef.current);
        await delay(TONEARM_SWING_DURATION);

        setTonearmLifted(false);
        await delay(TONEARM_DROP_DURATION);

        if (audioRef.current) {
          try {
            await audioRef.current.play();
            setState('playing');
          } catch (error) {
            console.error('Playback failed:', error);
            setState('paused');
          }
        }
      }
    } finally {
      busyRef.current = false;
    }
  }, [currentAlbum, state]);

  const pause = useCallback(async () => {
    if (state !== 'playing' || busyRef.current) return;
    busyRef.current = true;
    try {
      setState('paused');
      
      savedAngleRef.current = tonearmAngle;

      setTonearmLifted(true);
      
      if (audioRef.current) {
        audioRef.current.pause();
      }

      await delay(TONEARM_LIFT_DURATION);
      setTonearmAngle(TONEARM_REST_ANGLE);

      await delay(PLATTER_DECEL_DURATION);
      setIsSpinning(false);
    } finally {
      busyRef.current = false;
    }
  }, [state, tonearmAngle]);

  const seekTrack = useCallback(async (index: number) => {
    if (!currentAlbum || index < 0 || index >= currentAlbum.tracks.length) return;
    if (index === currentTrackIndex && state === 'playing') return;
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      const wasPlaying = state === 'playing';
      setState('seeking');

      setTonearmLifted(true);
      await delay(TONEARM_LIFT_DURATION);

      setCurrentTrackIndex(index);
      setProgress(0);

      const newAngle = getTonearmAngle(index, 0, currentAlbum.tracks);
      setTonearmAngle(newAngle);
      await delay(TONEARM_SWING_DURATION);

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = currentAlbum.tracks[index].src;
        audioRef.current.currentTime = 0;
      } else {
        audioRef.current = new Audio(currentAlbum.tracks[index].src);
        audioRef.current.volume = volumeRef.current;
      }

      audioRef.current.onended = () => {
        if (index < currentAlbum.tracks.length - 1) {
          seekTrack(index + 1);
        } else {
          pause();
        }
      };

      setTonearmLifted(false);
      await delay(TONEARM_DROP_DURATION);

      if (wasPlaying) {
        try {
          await audioRef.current.play();
          setState('playing');
        } catch (error) {
          console.error('Playback failed:', error);
          setState('paused');
        }
      } else {
        setState('paused');
      }
    } finally {
      busyRef.current = false;
    }
  }, [currentAlbum, currentTrackIndex, state, pause]);

  const stop = useCallback(async (fast = false) => {
    await unloadVinyl(fast);
  }, [unloadVinyl]);

  const togglePlayPause = useCallback(async () => {
    if (state === 'playing') {
      await pause();
    } else if (state === 'paused' || state === 'cueing') {
      await play();
    }
  }, [state, play, pause]);

  const setHoveredAlbum = useCallback((albumId: string | null) => {
    setHoveredAlbumId(albumId);
  }, []);

  // Update audio volume when volume state changes
  const updateVolume = useCallback((newVolume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    volumeRef.current = clampedVolume;
    setVolume(clampedVolume);
    if (audioRef.current) {
      audioRef.current.volume = clampedVolume;
    }
  }, []);

  // Seek to a specific tonearm angle position
  // Returns true if the angle is within vinyl range, false otherwise
  // Scratch: crossfade music out, return a resume callback that crossfades back in
  const scratch = useCallback(() => {
    if (!audioRef.current || state !== 'playing') return null;
    const audio = audioRef.current;
    const originalVolume = audio.volume;
    
    // Fade out over 500ms
    const fadeOutSteps = 10;
    const fadeOutInterval = 500 / fadeOutSteps;
    let step = 0;
    const fadeOut = setInterval(() => {
      step++;
      audio.volume = originalVolume * (1 - step / fadeOutSteps);
      if (step >= fadeOutSteps) {
        clearInterval(fadeOut);
        audio.pause();
        audio.volume = originalVolume;
      }
    }, fadeOutInterval);

    return () => {
      if (audioRef.current) {
        audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 3);
        audioRef.current.volume = 0;
        audioRef.current.play().catch(console.error);
        // Fade in over 500ms
        const fadeInSteps = 10;
        const fadeInInterval = 500 / fadeInSteps;
        let s = 0;
        const fadeIn = setInterval(() => {
          s++;
          if (audioRef.current) {
            audioRef.current.volume = originalVolume * (s / fadeInSteps);
          }
          if (s >= fadeInSteps) clearInterval(fadeIn);
        }, fadeInInterval);
      }
    };
  }, [state]);

  const seekToAngle = useCallback(async (angle: number): Promise<boolean> => {
    if (!currentAlbum || busyRef.current) return false;
    busyRef.current = true;
    // Remember state before seeking so we don't flash through 'seeking'
    const prevState = state;
    try {
      // Set angle immediately so when isDragging clears, display stays put
      setTonearmAngle(angle);

      // Check if angle is within vinyl range — just pause, don't unload
      if (angle > TONEARM_START_ANGLE || angle < TONEARM_END_ANGLE) {
        if (audioRef.current) {
          audioRef.current.pause();
        }
        setTonearmLifted(true);
        setIsSpinning(false);
        setTonearmAngle(TONEARM_REST_ANGLE);
        setState('paused');
        return false;
      }
      
      const overallProgress = (TONEARM_START_ANGLE - angle) / TONEARM_RANGE;
      const totalDuration = getTotalDuration(currentAlbum.tracks);
      const targetTime = overallProgress * totalDuration;
      
      let cumulativeTime = 0;
      let targetTrackIndex = 0;
      let timeInTrack = 0;
      
      for (let i = 0; i < currentAlbum.tracks.length; i++) {
        const trackDuration = currentAlbum.tracks[i].duration;
        if (cumulativeTime + trackDuration > targetTime) {
          targetTrackIndex = i;
          timeInTrack = targetTime - cumulativeTime;
          break;
        }
        cumulativeTime += trackDuration;
      }
      
      // Don't set state to 'seeking' — keep current state to avoid button flicker
      setTonearmLifted(true);
      await delay(TONEARM_LIFT_DURATION);
      
      setCurrentTrackIndex(targetTrackIndex);
      // Skip swing delay — arm is already at the correct position from drag
      
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = currentAlbum.tracks[targetTrackIndex].src;
      } else {
        audioRef.current = new Audio(currentAlbum.tracks[targetTrackIndex].src);
      }
      
      await new Promise<void>((resolve) => {
        if (!audioRef.current) { resolve(); return; }
        const audio = audioRef.current;
        const handleCanPlay = () => {
          audio.removeEventListener('canplay', handleCanPlay);
          resolve();
        };
        audio.addEventListener('canplay', handleCanPlay);
        audio.load();
      });
      
      if (audioRef.current) {
        audioRef.current.currentTime = timeInTrack;
        audioRef.current.volume = volume;
        audioRef.current.onended = () => {
          if (targetTrackIndex < currentAlbum.tracks.length - 1) {
            seekTrack(targetTrackIndex + 1);
          } else {
            pause();
          }
        };
      }
      
      setTonearmLifted(false);
      await delay(TONEARM_DROP_DURATION);
      
      if (audioRef.current) {
        try {
          await audioRef.current.play();
          setState('playing');
          setIsSpinning(true);
        } catch (error) {
          console.error('Playback failed:', error);
          setState('paused');
        }
      }
      
      return true;
    } finally {
      busyRef.current = false;
    }
  }, [currentAlbum, state, volume, seekTrack, pause]);

  // Set tonearm angle directly (for dragging preview)
  const setTonearmAngleDirectly = useCallback((angle: number) => {
    setTonearmAngle(angle);
  }, []);

  // Lift/lower tonearm
  const setTonearmLiftedDirectly = useCallback((lifted: boolean) => {
    setTonearmLifted(lifted);
  }, []);

  // Seek within the current track (progress 0-1)
  const seekInTrack = useCallback((trackProgressTarget: number) => {
    if (!currentAlbum || !audioRef.current) return;
    
    const currentTrack = currentAlbum.tracks[currentTrackIndex];
    if (!currentTrack) return;
    
    const targetTime = trackProgressTarget * currentTrack.duration;
    audioRef.current.currentTime = targetTime;
    setProgress(trackProgressTarget);
    
    // Update tonearm angle
    const newAngle = getTonearmAngle(currentTrackIndex, trackProgressTarget, currentAlbum.tracks);
    setTonearmAngle(newAngle);
  }, [currentAlbum, currentTrackIndex]);

  // Busy during any transitional state (cueing, seeking, selecting, loading, unloading)
  const isBusy = state === 'cueing' || state === 'seeking' || state === 'selecting' || state === 'loading' || state === 'unloading';

  return {
    state,
    currentAlbum,
    previousAlbum,
    currentTrackIndex,
    progress,
    isSpinning,
    vinylPosition,
    tonearmAngle,
    tonearmLifted,
    hoveredAlbumId,
    volume,
    isBusy,
    selectAlbum,
    play,
    pause,
    scratch,
    seekTrack,
    seekInTrack,
    seekToAngle,
    setTonearmAngleDirectly,
    setTonearmLiftedDirectly,
    stop,
    togglePlayPause,
    setHoveredAlbum,
    setVolume: updateVolume,
  };
}
